import { Listbox, Transition } from "@headlessui/react";
import { truncate, size, trim, get } from "lodash";
import { Fragment } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import AwesomeIcon from "../AwesomeIcon";
import { faChevronUp } from "@fortawesome/pro-solid-svg-icons";
import { getTrimmedWorkspaceId } from "../../utils";
import { useFloatingPortal } from "../../hooks/useFloatingPortal";

const Dropdown = (props) => {
  const {
    options,
    selectedOption,
    onChange,
    hasBorder,
    isLeftAligned,
    className = "",
    isBottomAligned,
    disabled = false,
    iconClassName,
    parentClassName = "",
    placeholder,
    menuClassName = "",
    portal = false,
    disableTruncate = false,
  } = props;
  let selectedLabel = get(selectedOption, ["label"], "");
  if (!disableTruncate) selectedLabel = truncate(selectedLabel, { length: 20 });

  const { buttonRef, setFloatingRef } = useFloatingPortal(portal);

  const optionsList = (open) => (
    <Listbox.Options
      ref={portal ? setFloatingRef : undefined}
      static={portal ? true : undefined}
      className={classNames(
        "flex flex-col max-h-[170px] overflow-y-auto scrollbar-light dark:scrollbar bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-500 rounded-md text-left",
        portal && "text-[12px]",
        portal ? "z-[9999]" : "absolute z-50",
        menuClassName || (portal ? "min-w-[120px]" : "w-full"),
        !portal && {
          "right-0": !isLeftAligned,
          "bottom-[50px]": !isBottomAligned,
        }
      )}
    >
      {options &&
        options?.map((opts, index) => {
          return (
            <Listbox.Option key={opts?.id || index} value={opts} as={Fragment}>
              {({ active, selected }) => (
                <div
                  className={classNames(
                    portal ? "px-3 py-1.5" : "px-4 py-4",
                    "cursor-pointer",
                    {
                      "border-b border-b-gray-500":
                        !portal && size(options) - 1 !== index,
                      "bg-teal-500 text-gray-100 dark:bg-teal-800": active || selected,
                    }
                  )}
                >
                  <span className={portal ? "break-words" : undefined}>{opts.label}</span>
                </div>
              )}
            </Listbox.Option>
          );
        })}
    </Listbox.Options>
  );

  return (
    <Listbox
      as="div"
      className={classNames("relative", {
        "w-full": !portal,
        "w-auto": portal,
        [parentClassName]: !!parentClassName,
      })}
      value={selectedOption}
      onChange={onChange}
    >
      {({ open }) => (
        <>
          <Listbox.Button
            ref={buttonRef}
            className={classNames(
              "px-4 py-3 w-full rounded-md flex items-center justify-between gap-4 text-gray-600 dark:text-gray-400 outline-none",
              {
                "border border-gray-500": hasBorder,
                "cursor-not-allowed": disabled,
                [className]: !!className,
              }
            )}
            aria-disabled={disabled}
          >
            <span>
              {selectedLabel || placeholder}
              <span
                className={classNames("uppercase hidden ", {
                  "sm:inline-block": !!selectedOption?.id,
                })}
              >
                | {getTrimmedWorkspaceId(selectedOption?.id)}
              </span>
            </span>
            <div className="flex items-center gap-2">
              <AwesomeIcon
                name={faChevronUp}
                className={classNames("opacity-50 rotate-180", {
                  "!rotate-0": open,
                  hidden: disabled,
                  [iconClassName]: !!iconClassName,
                })}
              />
            </div>
          </Listbox.Button>

          {portal ? (
            open && createPortal(optionsList(open), document.body)
          ) : (
            <Transition
              enter="transition duration-100 ease-out"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-75 ease-out"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0"
            >
              {optionsList(open)}
            </Transition>
          )}
        </>
      )}
    </Listbox>
  );
};

export default Dropdown;
